import { useEffect, useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminGetPropertyContacts, adminUpdatePropertyContact } from '@/lib/api';
import { toast } from 'sonner';
import { Phone, ShieldCheck, AlertTriangle, Save, X, Pencil, Loader2, Search } from 'lucide-react';

const PropertyContactsManager = () => {
  const [data, setData] = useState({ items: [], total: 0, pending_review: 0, missing_number: 0 });
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [orig, setOrig] = useState({});
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await adminGetPropertyContacts(status, q)); }
    catch { toast.error('Could not load property contacts'); }
    finally { setLoading(false); }
  }, [status, q]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => {
    const base = { owner_name: p.owner_name || '', owner_phone: p.owner_phone || '', owner_whatsapp: p.owner_whatsapp || '' };
    setEditId(p.property_id); setForm(base); setOrig(base);
  };
  const save = async (id) => {
    setBusy(id);
    try {
      // Send only fields the admin actually changed, so unchanged WhatsApp
      // is left for the backend to derive from the phone.
      const payload = {};
      Object.keys(form).forEach((k) => { if (form[k] !== orig[k]) payload[k] = form[k]; });
      if (Object.keys(payload).length === 0) { setEditId(null); setBusy(null); return; }
      await adminUpdatePropertyContact(id, payload);
      toast.success('Contact updated');
      setEditId(null);
      await load();
    } catch (e) { toast.error(e.message || 'Failed to update'); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="admin-property-contacts">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-3 text-sm">
          <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700">Total: {data.total}</span>
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Verified: {data.total - data.pending_review}</span>
          <span className="px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 inline-flex items-center gap-1" data-testid="pending-count"><AlertTriangle className="h-3.5 w-3.5" /> Needs review: {data.pending_review}</span>
        </div>
        <div className="flex gap-2 ml-auto">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search title, developer, city" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 w-64" data-testid="contacts-search" />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="contacts-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All listings</SelectItem>
              <SelectItem value="verified">Verified only</SelectItem>
              <SelectItem value="pending_review">Needs review</SelectItem>
              <SelectItem value="missing">Missing number</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left">
              <tr>
                <th className="p-3 font-medium">Property</th>
                <th className="p-3 font-medium">Seller / Developer</th>
                <th className="p-3 font-medium">Phone</th>
                <th className="p-3 font-medium">WhatsApp</th>
                <th className="p-3 font-medium">Status</th>
                <th className="p-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((p) => {
                const editing = editId === p.property_id;
                return (
                  <tr key={p.property_id} className="border-t border-slate-100 align-top" data-testid={`contact-row-${p.property_id}`}>
                    <td className="p-3 max-w-xs"><p className="font-medium text-[#0F172A] truncate">{p.title}</p><p className="text-xs text-slate-400">{p.city} · {p.property_id}</p></td>
                    <td className="p-3">{editing ? <Input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="w-44" data-testid="edit-owner-name" /> : <span>{p.owner_name || p.developer || '—'}</span>}</td>
                    <td className="p-3">{editing ? <Input value={form.owner_phone} onChange={(e) => setForm({ ...form, owner_phone: e.target.value })} className="w-40" placeholder="+91…" data-testid="edit-owner-phone" /> : (p.owner_phone ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3 text-slate-400" />{p.owner_phone}</span> : <span className="text-rose-500">none</span>)}</td>
                    <td className="p-3">{editing ? <Input value={form.owner_whatsapp} onChange={(e) => setForm({ ...form, owner_whatsapp: e.target.value })} className="w-36" placeholder="91…" data-testid="edit-owner-wa" /> : (p.owner_whatsapp || <span className="text-slate-400">—</span>)}</td>
                    <td className="p-3">{p.contact_status === 'verified' ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Verified</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Review</span>}</td>
                    <td className="p-3 text-right">
                      {editing ? (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" disabled={busy === p.property_id} onClick={() => save(p.property_id)} className="rounded-full bg-[#0F172A] text-white" data-testid={`save-contact-${p.property_id}`}>{busy === p.property_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}</Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditId(null)}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => startEdit(p)} data-testid={`edit-contact-${p.property_id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PropertyContactsManager;
